import { DateTime } from 'luxon'

import { useStoryReader } from '@hooks/book/useStoryReader'
import { BookReadFreePromotionDetailType } from '@models/chapter/BookReadFreePromotionDetailType'
import { PromotionReadFreeEnum } from '@interfaces/PromotionTypeEnum'
import { formatCountdown, promotionDate } from '@lib/promotion/utils'

export function useReadFreePromotion(data?: BookReadFreePromotionDetailType) {
  const { isOwner } = useStoryReader()

  function date(d: string) {
    return DateTime.fromISO(d).toUTC()
  }

  function getTodayReadCount(
    now: DateTime,
    replaceData?: BookReadFreePromotionDetailType
  ) {
    if (!data && !replaceData) return 0
    const alreadyReadChapter =
      replaceData?.alreadyReadChapter || data?.alreadyReadChapter || []
    return alreadyReadChapter.filter(
      param =>
        now.startOf('day').valueOf() ===
        date(param.createdAt).startOf('day').valueOf()
    ).length
  }

  function isReadFreePromotion(chapterId: number): boolean {
    if (data) {
      const { currentReadPromotion, alreadyReadChapter } = data
      const {
        startDate,
        endDate,
        readPromotionType,
        readFreePerDay,
        readFreeWithinDay,
        readFreeIntervalEnd,
        readFreeIntervalStart,
      } = currentReadPromotion

      const now = DateTime.now().toUTC()
      const isAlreadyRead = alreadyReadChapter
        .filter(
          param =>
            param.chapterId === chapterId &&
            date(param.expiredAt).valueOf() > now.valueOf()
        )
        .at(-1)
      const isSameChapter = isAlreadyRead?.chapterId === chapterId

      const convertedExpiredAt = DateTime.fromISO(
        isAlreadyRead?.expiredAt ?? ''
      ).toUTC()

      if (isOwner) return false

      const inPromotionPeriod =
        now.valueOf() >= date(startDate).valueOf() &&
        now.valueOf() <= date(endDate).valueOf()

      if (readPromotionType === PromotionReadFreeEnum.READ_FREE_PER_DAY) {
        const todayReadCount = getTodayReadCount(now)
        const minutesRemaining =
          convertedExpiredAt.diff(now, 'minutes')?.minutes || 0

        if (inPromotionPeriod || isSameChapter) {
          return minutesRemaining > 0 || todayReadCount < readFreePerDay
        }

        return false
      }

      if (readPromotionType === PromotionReadFreeEnum.READ_FREE_WITHIN_DAY) {
        const isAlreadyRead = alreadyReadChapter
          .filter(param => date(param.expiredAt).valueOf() > now.valueOf())
          .at(-1)
        if (!inPromotionPeriod && !isAlreadyRead) return false

        const convertedExpiredAt = DateTime.fromISO(
          isAlreadyRead?.expiredAt ?? ''
        ).toUTC()
        const minutesRemaining =
          convertedExpiredAt.diff(now, 'minutes')?.minutes || 0
        return (
          !isAlreadyRead ||
          (isAlreadyRead &&
            minutesRemaining > 0 &&
            now.valueOf() <= convertedExpiredAt.valueOf())
        )
      }

      if (readPromotionType === PromotionReadFreeEnum.READ_FREE_INTERVAL) {
        if (!inPromotionPeriod) return false
        return (
          now.valueOf() >= date(readFreeIntervalStart).valueOf() &&
          now.valueOf() <= date(readFreeIntervalEnd).valueOf()
        )
      }
    }

    return false
  }

  function showReadFreePerDay(chapterId: number) {
    if (data) {
      const { currentReadPromotion, alreadyReadChapter } = data
      const { readFreePerDay, startDate, endDate } = currentReadPromotion

      const now = DateTime.now().toUTC()
      const isAlreadyRead = alreadyReadChapter
        .filter(
          param =>
            param.chapterId === chapterId &&
            date(param.expiredAt).valueOf() > now.valueOf()
        )
        .at(-1)
      const todayReadCount = getTodayReadCount(now)
      const diff = DateTime.fromISO(isAlreadyRead?.expiredAt || '')
        .toUTC()
        .diff(now, ['days', 'hours', 'minutes', 'seconds'])
      const { days, hours, minutes, seconds } = diff.toObject()
      const countDownFormat = formatCountdown({ days, hours, minutes, seconds })

      // อ่านฟรีวันละ : กรณีที่ user กดอ่านไปแล้ว และเวลานับถอยหลังยังไม่หมดอายุ
      if (isAlreadyRead && !!countDownFormat) {
        return (
          <>
            <span>เหลือเวลา {diff.toFormat(countDownFormat)}</span>
          </>
        )
      }

      const inPromotionPeriod =
        now.valueOf() >= date(startDate).valueOf() &&
        now.valueOf() <= date(endDate).valueOf()

      if (!inPromotionPeriod) return null

      // อ่านฟรีวันละ : กรณีที่ user ยังไม่ได้อ่านและยังเหลือจำนวนสิทธิ์อ่านฟรีอยู่
      if (todayReadCount < readFreePerDay) {
        return (
          <span>
            รับสิทธิ์อ่านฟรีวันละ {currentReadPromotion.readFreePerDay} ตอน
            (ใช้สิทธิ์ <strong className='font-bold'>{todayReadCount}</strong>/
            {readFreePerDay})
          </span>
        )
      }
    }

    return null
  }

  function showReadFreeWithInDay(chapterId: number) {
    if (data) {
      const { currentReadPromotion, alreadyReadChapter } = data
      const { readFreeWithinDay } = currentReadPromotion

      const now = DateTime.now().toUTC()
      const isAlreadyRead = !!alreadyReadChapter.filter(
        param => date(param.expiredAt).valueOf() > now.valueOf()
      ).length
      const convertedExpiredAt = DateTime.fromISO(
        alreadyReadChapter[0]?.expiredAt || ''
      ).toUTC()
      const diff = convertedExpiredAt.diff(now, [
        'days',
        'hours',
        'minutes',
        'seconds',
      ])
      const { days, hours, minutes, seconds } = diff.toObject()
      const countDownFormat = formatCountdown({ days, hours, minutes, seconds })

      // อ่านฟรีภายใน : กรณีที่ user ยังไม่ได้อ่าน
      if (!isAlreadyRead) {
        return <span>รับสิทธิ์อ่านฟรีภายใน {readFreeWithinDay} วัน</span>
      }

      // อ่านฟรีภายใน : กรณีที่ user กดอ่านแล้ว และอยู่ในช่วงเวลาที่กำหนด
      if (
        isAlreadyRead &&
        !!countDownFormat &&
        now.valueOf() <= convertedExpiredAt.valueOf()
      ) {
        return <span>อ่านฟรีภายใน {diff.toFormat(countDownFormat)}</span>
      }
    }

    return null
  }

  function showReadFreeInterval() {
    if (data) {
      const { currentReadPromotion } = data
      const { readFreeIntervalStart, readFreeIntervalEnd } =
        currentReadPromotion

      const now = DateTime.now().toUTC()

      if (
        now.valueOf() >= date(readFreeIntervalStart).valueOf() &&
        now.valueOf() <= date(readFreeIntervalEnd).valueOf()
      ) {
        const dates = promotionDate(
          readFreeIntervalStart,
          readFreeIntervalEnd,
          '',
          true
        ).split(' - ')
        return <span>อ่านฟรี ถึง {dates.length > 1 ? dates[1] : dates[0]}</span>
      }
    }

    return null
  }

  return {
    isReadFreePromotion,
    showReadFreePerDay,
    showReadFreeWithInDay,
    showReadFreeInterval,
    getTodayReadCount,
  }
}
